import type { Dayjs } from 'dayjs';
import type { EmpleadoAlta, EmpleadoDatos, EmpleadoFicha, RolCatalogo } from '../../api/empleadosApi';

/** Anchos máximos de las columnas de Gescom (el validador del servidor los repite). */
export const LIMITES = {
  codigoCorto: 5,
  nombreCompleto: 255,
  nombreAbreviado: 255,
  puesto: 100,
  departamento: 100,
  telefono: 20,
  emailEmpresarial: 255,
} as const;

/** Mayor entero que admite el servidor (Int32) en los niveles. */
export const MAX_ENTERO = 2147483647;

/** Dominio del email de empresa. El servidor manda (`Empleados:DominiosEmail`); aquí solo se avisa antes. */
export const DOMINIO_EMAIL = '@farmaconsulting.es';

/** `null` si el email es válido; si no, el motivo en castellano. */
export const errorEmail = (valor?: string | null): string | null => {
  const email = (valor ?? '').trim();
  if (!email) return 'El email es obligatorio.';
  if (email.length > LIMITES.emailEmpresarial) return `El email no puede superar ${LIMITES.emailEmpresarial} caracteres.`;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'El email no tiene un formato válido.';
  if (!email.toLowerCase().endsWith(DOMINIO_EMAIL)) return `El email tiene que ser de ${DOMINIO_EMAIL.slice(1)}.`;
  return null;
};

/** Texto recortado; vacío = `null` (en la edición, `null` vacía el campo). */
export const aTexto = (valor?: string | null): string | null => {
  const t = (valor ?? '').trim();
  return t ? t : null;
};

const aFecha = (valor?: Dayjs | null): string | null => (valor ? valor.format('YYYY-MM-DD') : null);
const aNumero = (valor?: number | null): number | null => (valor === undefined || valor === null ? null : valor);

/** Valores del formulario profesional (los de los campos de antd). */
export interface ValoresEmpleado {
  codigoCorto?: string;
  nombreCompleto?: string;
  nombreAbreviado?: string;
  puesto?: string;
  departamento?: string;
  telefono?: string;
  emailEmpresarial?: string;
  idDelegacion?: number;
  idZona?: number;
  nivelSeguridad?: number;
  nivelAsesor?: number;
  nivelInterlocucion?: number;
  accesoGdgo?: boolean;
  asignarCcl?: boolean;
  controlAcompaniamiento?: boolean;
  fechaAlta?: Dayjs;
  roles?: string[];
}

/** Cuerpo de la edición: sustitución total, así que cada campo va siempre (vacío = `null`). */
export const aDatosEmpleado = (v: ValoresEmpleado): EmpleadoDatos => ({
  codigoCorto: aTexto(v.codigoCorto),
  nombreCompleto: (v.nombreCompleto ?? '').trim(),
  nombreAbreviado: aTexto(v.nombreAbreviado),
  puesto: aTexto(v.puesto),
  departamento: aTexto(v.departamento),
  telefono: aTexto(v.telefono),
  emailEmpresarial: (v.emailEmpresarial ?? '').trim(),
  idDelegacion: v.idDelegacion as number,
  idZona: aNumero(v.idZona),
  nivelSeguridad: v.nivelSeguridad as number,
  nivelAsesor: aNumero(v.nivelAsesor),
  nivelInterlocucion: aNumero(v.nivelInterlocucion),
  accesoGdgo: !!v.accesoGdgo,
  asignarCcl: !!v.asignarCcl,
  controlAcompaniamiento: !!v.controlAcompaniamiento,
});

export const aAlta = (v: ValoresEmpleado): EmpleadoAlta => ({
  ...aDatosEmpleado(v),
  fechaAlta: aFecha(v.fechaAlta),
  roles: v.roles ?? [],
});

/** Los valores de la ficha para abrir el formulario de edición. */
export const valoresDesdeFicha = (f: EmpleadoFicha): ValoresEmpleado => ({
  codigoCorto: f.codigoCorto ?? undefined,
  nombreCompleto: f.nombreCompleto,
  nombreAbreviado: f.nombreAbreviado ?? undefined,
  puesto: f.puesto ?? undefined,
  departamento: f.departamento ?? undefined,
  telefono: f.telefono ?? undefined,
  emailEmpresarial: f.emailEmpresarial ?? undefined,
  idDelegacion: f.idDelegacion ?? undefined,
  idZona: f.idZona ?? undefined,
  nivelSeguridad: f.nivelSeguridad ?? undefined,
  nivelAsesor: f.nivelAsesor ?? undefined,
  nivelInterlocucion: f.nivelInterlocucion ?? undefined,
  accesoGdgo: f.accesoGdgo,
  asignarCcl: f.asignarCcl,
  controlAcompaniamiento: f.controlAcompaniamiento,
});

// ───────────────────────────── Nivel de interlocución (D-EMP-17)

/** Los tres niveles que conoce la pantalla; en la API sigue siendo un entero. */
export const NIVELES_INTERLOCUCION = [
  { value: 1110, label: 'Bajo' },
  { value: 1111, label: 'Medio' },
  { value: 1112, label: 'Alto' },
] as const;

/** La palabra del nivel, «Valor N» si es otro número y `null` si no hay nivel. */
export const etiquetaInterlocucion = (valor?: number | null): string | null => {
  if (valor === undefined || valor === null) return null;
  return NIVELES_INTERLOCUCION.find((n) => n.value === valor)?.label ?? `Valor ${valor}`;
};

/** Bajo, medio y alto, más el valor que ya tenía la ficha si es otro: así se ve, se puede dejar y se conserva al guardar. */
export const opcionesInterlocucion = (inicial?: number | null): { value: number; label: string }[] => {
  const opciones: { value: number; label: string }[] = NIVELES_INTERLOCUCION.map((n) => ({ value: n.value, label: n.label }));
  if (inicial !== undefined && inicial !== null && !opciones.some((o) => o.value === inicial)) {
    opciones.push({ value: inicial, label: `Valor ${inicial}` });
  }
  return opciones;
};

// ───────────────────────────── Roles por su nombre (D-EMP-17)

/** El nombre del rol, o «Sin nombre: <código>» si el catálogo no lo conoce. */
export const nombreRol = (guidRol: string, nombre?: string | null): string => nombre?.trim() || `Sin nombre: ${guidRol}`;

/**
 * Opciones del selector de roles: nombre y, entre paréntesis, cuántos empleados lo tienen. Los roles que ya tiene el
 * empleado y no están en el catálogo se añaden por su código, para no perderlos al guardar.
 */
export const opcionesRoles = (catalogo: RolCatalogo[] = [], actuales: string[] = []): { value: string; label: string }[] => {
  const opciones = catalogo.map((r) => ({ value: r.guidRol, label: `${nombreRol(r.guidRol, r.nombre)} (${r.empleados})` }));
  actuales.filter((g) => !catalogo.some((r) => r.guidRol === g)).forEach((g) => opciones.push({ value: g, label: nombreRol(g) }));
  return opciones;
};

// ───────────────────────────── Errores del servidor, en su campo

/** Un error del servidor que se pinta bajo su campo. */
export interface ErrorDeCampo { campo: keyof ValoresEmpleado; mensaje: string }

/** Texto fijo para los errores técnicos de binding del servidor (`$.campo`, `request`), que no se enseñan tal cual. */
export const VALOR_NO_VALIDO = 'El valor no es válido.';

/** El 51402 sirve para el email y también para «nombre vacío o demasiado largo»: se decide por el mensaje. */
const campoDe51402 = (mensaje: string): keyof ValoresEmpleado => (/nombre/i.test(mensaje) ? 'nombreCompleto' : 'emailEmpresarial');

/** Errores de negocio 514xx (CONTRATO §3) que tienen campo. */
const CAMPO_POR_CODIGO: Record<number, keyof ValoresEmpleado> = {
  51408: 'emailEmpresarial', 51410: 'emailEmpresarial',
  51403: 'idDelegacion', 51404: 'idZona', 51405: 'nivelSeguridad', 51409: 'roles',
};

const CAMPOS: string[] = [
  'codigoCorto', 'nombreCompleto', 'nombreAbreviado', 'puesto', 'departamento', 'telefono', 'emailEmpresarial',
  'idDelegacion', 'idZona', 'nivelSeguridad', 'nivelAsesor', 'nivelInterlocucion', 'accesoGdgo', 'asignarCcl',
  'controlAcompaniamiento', 'fechaAlta', 'roles',
];
const ALIAS: Record<string, keyof ValoresEmpleado> = { email: 'emailEmpresarial', nombre: 'nombreCompleto' };

const campoDeClave = (clave: string): keyof ValoresEmpleado | null => {
  const ultima = clave.split('.').pop() ?? '';
  const k = ultima.charAt(0).toLowerCase() + ultima.slice(1);
  return ALIAS[k] ?? (CAMPOS.includes(k) ? (k as keyof ValoresEmpleado) : null);
};

/**
 * Reparte el error de una escritura entre campos y el resto. Un 422 con código de campo (`extensions.codigo`) y un 400
 * cuya clave es un campo van a su campo; lo demás se queda en `resto` para el aviso del formulario.
 */
export const erroresPorCampo = (error: unknown): { campos: ErrorDeCampo[]; resto: string[] } => {
  const respuesta = (error as { response?: { status?: number; data?: { detail?: string; title?: string; codigo?: number; errors?: Record<string, string[]> } } })?.response;
  const data = respuesta?.data;
  if (!respuesta || !data) return { campos: [], resto: [] };
  if (respuesta.status === 422 && data.codigo !== undefined && CAMPO_POR_CODIGO[data.codigo] && (data.detail || data.title)) {
    const mensaje = (data.detail || data.title) as string;
    return { campos: [{ campo: CAMPO_POR_CODIGO[data.codigo], mensaje }], resto: [] };
  }
  if (respuesta.status === 422 && data.codigo === 51402 && (data.detail || data.title)) {
    const mensaje = (data.detail || data.title) as string;
    return { campos: [{ campo: campoDe51402(mensaje), mensaje }], resto: [] };
  }
  if (respuesta.status === 400 && data.errors) {
    const campos: ErrorDeCampo[] = [];
    const resto: string[] = [];
    Object.entries(data.errors).forEach(([clave, mensajes]) => {
      const campo = campoDeClave(clave);
      // Las claves técnicas (`$.nivelSeguridad`, `request`) traen texto del serializador, en inglés: se cambia por uno fijo.
      const tecnica = clave.startsWith('$') || clave.toLowerCase() === 'request';
      (tecnica ? [VALOR_NO_VALIDO] : mensajes).forEach((mensaje) => {
        if (campo) campos.push({ campo, mensaje });
        else if (!resto.includes(mensaje)) resto.push(mensaje);
      });
    });
    return { campos, resto };
  }
  return { campos: [], resto: [] };
};
